import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import { api, ErroApi } from './cliente';
import { chaves } from './query';
import type { Usuario } from './tipos';

/* Sessão.
 *
 * O produto NÃO tem tela de login obrigatória: quem abre a ferramenta já pode
 * usar. Se não houver cookie de sessão, criamos uma sessão anônima e o
 * trabalho começa; virar conta de verdade (e-mail + senha) é um upgrade do
 * MESMO usuário, preservando tudo que já foi preenchido. */

/* A API envelopa: toda rota de auth devolve `{ usuario }`, nunca o usuário
 * cru. `/auth/eu` inclusive responde `{ usuario: null }` com 200 quando não há
 * sessão, em vez de 401. Desembrulhamos aqui, num ponto só, para o resto do
 * front continuar lidando com `Usuario | null`. */
type RespostaAuth = { usuario: Usuario | null };

export function useEu() {
  return useQuery({
    queryKey: chaves.eu,
    queryFn: () => api.obter<RespostaAuth>('/auth/eu').then((r) => r.usuario),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/**
 * Garante que existe sessão antes de a aplicação desenhar.
 *
 * A criação de sessão anônima só é tentada UMA vez por carregamento (a trava
 * do ref): sem ela, um 401 persistente viraria um laço de POSTs.
 */
export function useGarantirSessao() {
  const clienteQuery = useQueryClient();
  const eu = useEu();
  const jaTentou = React.useRef(false);

  const criarAnonimo = useMutation({
    mutationFn: () => api.criar<RespostaAuth>('/auth/anonimo').then((r) => r.usuario),
    onSuccess: (usuario) => clienteQuery.setQueryData(chaves.eu, usuario),
  });

  // Sem sessão pode chegar de dois jeitos: `{ usuario: null }` com 200 (o
  // caminho normal) ou 401 (se a rota passar a exigir sessão um dia).
  const semSessao =
    (eu.isSuccess && eu.data === null) ||
    (eu.isError && eu.error instanceof ErroApi && eu.error.naoAutenticado);

  React.useEffect(() => {
    if (semSessao && !jaTentou.current) {
      jaTentou.current = true;
      criarAnonimo.mutate();
    }
  }, [semSessao, criarAnonimo]);

  return {
    usuario: eu.data ?? criarAnonimo.data ?? null,
    // "Pronto" é ter usuário. Enquanto não tem, a aplicação mostra carregando.
    pronto: Boolean(eu.data ?? criarAnonimo.data),
    carregando: eu.isPending || criarAnonimo.isPending,
    erro: semSessao ? (criarAnonimo.error ?? null) : (eu.error ?? null),
  };
}

export function useEntrar() {
  const clienteQuery = useQueryClient();
  return useMutation({
    mutationFn: (dados: { email: string; senha: string }) =>
      api.criar<RespostaAuth>('/auth/entrar', dados).then((r) => r.usuario),
    onSuccess: (usuario) => {
      clienteQuery.setQueryData(chaves.eu, usuario);
      // Trocou de usuário: tudo que estava em cache era do anterior.
      void clienteQuery.invalidateQueries();
    },
  });
}

/** Upgrade da sessão anônima para conta real, preservando os dados. */
export function useRegistrar() {
  const clienteQuery = useQueryClient();
  return useMutation({
    mutationFn: (dados: { email: string; senha: string; nome?: string }) =>
      api.criar<RespostaAuth>('/auth/registrar', dados).then((r) => r.usuario),
    onSuccess: (usuario) => {
      clienteQuery.setQueryData(chaves.eu, usuario);
      void clienteQuery.invalidateQueries();
    },
  });
}

export function useSair() {
  const clienteQuery = useQueryClient();
  return useMutation({
    mutationFn: () => api.criar<void>('/auth/sair'),
    onSuccess: () => clienteQuery.clear(),
  });
}
